import { Component, OnInit, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { IS_BROWSER_ONLY_ROUTE } from '@maximizer/oauth2/data-access';
import {
  InitializationService,
  OutlookService,
  NavigationService,
  OutlookStore,
  TokenMonitorService,
} from '@maximizer/outlook/shared/data-access';
import { OutlookMenuComponent } from '@maximizer/outlook/menu/ui';
import { SessionRefreshNoticeComponent } from '@maximizer/outlook/auth/ui';
import { MsalService } from '@azure/msal-angular';
import { InsightsService } from '@maximizer/core/shared/insights';

@Component({
  selector: 'maximizer-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterOutlet, OutlookMenuComponent, SessionRefreshNoticeComponent],
})
export class AppComponent implements OnInit {
  private readonly outlookService = inject(OutlookService);
  private readonly router = inject(Router);
  private readonly initializationService = inject(InitializationService);
  private readonly navigationService = inject(NavigationService);
  private readonly authService = inject(MsalService);
  private readonly insightsService = inject(InsightsService);
  private readonly isBrowserOnlyRoute = inject(IS_BROWSER_ONLY_ROUTE);
  private readonly outlookStore = inject(OutlookStore);

  constructor() {
    inject(TokenMonitorService);
  }

  pageTitle = this.navigationService.pageTitle;
  deeplink = this.navigationService.deeplink;

  showMenu = computed(() => this.navigationService.showMenu);
  isAuthorizedRoute = computed(() => this.navigationService.isAuthorizedRoute);
  canNavigateBack = computed(() => this.navigationService.canNavigateBack);

  ngOnInit(): void {
    if (this.isBrowserOnlyRoute) {
      // Browser tab — no Outlook host; MSAL/Office handlers don't apply.
      return;
    }

    this.authService.handleRedirectObservable().subscribe({
      error: (error) => {
        const exception =
          error instanceof Error
            ? error
            : new Error(String(error ?? 'Unknown redirect handling error'));
        this.insightsService.trackException(
          { error: exception },
          {
            component: 'AppComponent',
            event: 'app-component-msal-redirect-observable-failed',
          },
        );
      },
    });
    this.setupOutlookHandler();
  }

  private setupOutlookHandler(): void {
    this.outlookService?.mailbox?.addHandlerAsync(
      Office.EventType.ItemChanged,
      this.handleItemChanged.bind(this),
    );
  }

  async handleItemChanged(): Promise<void> {
    if (this.initializationService.initializingApp()) return;
    this.outlookStore.clearEntrySearchContext();
    await this.router.navigate(['/home']);
  }
}
